import { useEffect, useState } from "react";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { ProjectMember, ProjectPermission } from "../types";

interface CognitoUser {
  sub: string;
  email: string;
}

// Sharing page — administrators only. The route is accessible because the
// sidebar omits it for non-admins, but we also guard the API call here.

export default function SharingPage() {
  const { project } = useProject();
  const { call } = useApi();
  const [members, setMembers] = useState<ProjectMember[] | undefined>();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [selectedUser, setSelectedUser] = useState("");
  const [newPermission, setNewPermission] = useState<ProjectPermission>("reader");
  const [busy, setBusy] = useState(false);

  if (project.permission !== "administrator") {
    return <p className="error">Administrator permission required.</p>;
  }

  const refresh = async () => {
    try {
      setMembers(await call<ProjectMember[]>(`/api/projects/${project.projectId}/members`));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const loadUsers = async () => {
    try {
      setUsers(await call<CognitoUser[]>("/api/users"));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    Promise.all([refresh(), loadUsers()]).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.projectId]);

  // Filter out users who are already members.
  const memberSubs = new Set(members?.map((m) => m.userId));
  const availableUsers = users.filter((u) => !memberSubs.has(u.sub));

  const setPermission = async (userId: string, permission: ProjectPermission, email?: string) => {
    setBusy(true);
    try {
      await call(`/api/projects/${project.projectId}/members/${encodeURIComponent(userId)}`, {
        method: "PUT",
        body: JSON.stringify({ permission, email }),
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    if (!confirm(`Revoke access for ${userId}?`)) return;
    setBusy(true);
    try {
      await call(`/api/projects/${project.projectId}/members/${encodeURIComponent(userId)}`, {
        method: "DELETE",
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    const user = users.find((u) => u.sub === selectedUser);
    if (!user) return;
    await setPermission(user.sub, newPermission, user.email);
    setSelectedUser("");
    setNewPermission("reader");
  };

  // Resolve email for a member from the users list (fallback to stored email).
  const emailFor = (m: ProjectMember) => {
    const user = users.find((u) => u.sub === m.userId);
    return user?.email ?? m.email;
  };

  return (
    <>
      <div className="page-header">
        <h1>Sharing</h1>
        <p>Manage who can access this project and at what permission level.</p>
      </div>

      <div className="card">
        <h2>Add a user</h2>
        <div className="grid-3">
          <div className="field">
            <label>User</label>
            <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
              <option value="">— Select a user —</option>
              {availableUsers.map((u) => (
                <option key={u.sub} value={u.sub}>{u.email}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Permission</label>
            <select
              value={newPermission}
              onChange={(e) => setNewPermission(e.target.value as ProjectPermission)}
            >
              <option value="reader">Reader</option>
              <option value="contributor">Contributor</option>
              <option value="administrator">Administrator</option>
            </select>
          </div>
        </div>
        <div className="actions">
          <button disabled={busy || !selectedUser} onClick={addMember}>Add user</button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Permission</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!members && (
              <tr><td colSpan={3} className="muted" style={{ padding: 16 }}>Loading…</td></tr>
            )}
            {members?.map((m) => (
              <tr key={m.userId}>
                <td>{emailFor(m) ?? <span className="muted">{m.userId}</span>}</td>
                <td>
                  <select
                    value={m.permission}
                    disabled={busy}
                    onChange={(e) => setPermission(m.userId, e.target.value as ProjectPermission, emailFor(m))}
                  >
                    <option value="reader">Reader</option>
                    <option value="contributor">Contributor</option>
                    <option value="administrator">Administrator</option>
                  </select>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="secondary danger" disabled={busy} onClick={() => remove(m.userId)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
